// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import SumsubWebSdk from '@sumsub/websdk-react';
import {
    checkSumsubComplete,
    requestImageVerify,
    verifyPartnerAccount,
} from '../../services/registration';
import { getAccountId } from '../../utils/storage';
import './Sumsub.scss';
import { useNavigate } from 'react-router-dom';
import { getPartner } from '../../utils/storage';
import { handleApiError } from '../../utils/apiError';
import {
    readAccountId,
    redirectToPartnerOrConditions,
    resolvePartnerInfo,
    resolvePartnerRedirectUrl,
} from '../../utils/partnerRedirectFlow';
import { useTranslation } from 'react-i18next';
import useLogger from '../../hooks/useLogger';
import { SUMSUB_VERIFICATION_ACTIONS, LOG_STATUSES } from '../../logging/LoggingConstants';
import {
    AnalyticsEvent,
    durationSecSince,
    getBiometricStartedAt,
    markBiometricStarted,
    setAnalyticsVerificationMethod,
    trackBiometricVerificationFailed,
    trackRegistrationFailed,
    useAnalytics,
} from '../../analytics';
import RegistrationPage from '../../components/layouts/RegistrationPage';
import Loading from '../../components/loading/Loading';
import '../home/Terms.scss';
import '../emailVerification/Email.scss';
import { checkImIdEnabled } from '../../utils/featureFlags';

export default function Sumsub() {
    const accountId = getAccountId();
    const { t, i18n } = useTranslation();
    const [token, setToken] = useState('');
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const partner = getPartner();
    const { log } = useLogger();
    const { track } = useAnalytics();
    const applicantIdRef = useRef(null);
    const imageVerifyRequestedRef = useRef(false);
    const biometricStartedRef = useRef(false);

    useEffect(() => {
        if (!accountId || imageVerifyRequestedRef.current) {
            return;
        }
        imageVerifyRequestedRef.current = true;
        setAnalyticsVerificationMethod('sumsub');
        requestImageVerify(accountId)
            .then((response) => {
                log(SUMSUB_VERIFICATION_ACTIONS.SUMSUB_IMAGE_VERIFY, LOG_STATUSES.SUCCESS);
                setToken(response.token);
                if (!biometricStartedRef.current) {
                    biometricStartedRef.current = true;
                    markBiometricStarted();
                    track(AnalyticsEvent.BIOMETRIC_VERIFICATION_STARTED, {
                        verification_method: 'sumsub',
                    });
                }
            })
            .catch((e) => {
                imageVerifyRequestedRef.current = false;
                trackRegistrationFailed({
                    step: 'sumsub',
                    error: e,
                });
                handleApiError(e, navigate);
            });
    }, [accountId, log, navigate, track]);

    const handler = (name, e) => {
        if ('idCheck.onApplicantStatusChanged' === name && e.reviewStatus === 'completed') {
            setIsLoading(true);
            checkSumsubComplete(accountId, applicantIdRef.current)
                .then((response) => {
                    log(SUMSUB_VERIFICATION_ACTIONS.SUMSUB_CHECK_COMPLETE, LOG_STATUSES.SUCCESS);
                    if (response.success) {
                        track(AnalyticsEvent.BIOMETRIC_VERIFICATION_COMPLETED, {
                            verification_method: 'sumsub',
                            duration_sec:
                                durationSecSince(getBiometricStartedAt()) ?? 0,
                        });
                        const partnerRedirectUrl = resolvePartnerRedirectUrl(
                            response?.account,
                            partner
                        );
                        const partnerAccountId = readAccountId(response?.account) ?? accountId;
                        if (partnerRedirectUrl && partnerAccountId !== undefined) {
                            verifyPartnerAccount(partnerAccountId)
                                .then((res) => {
                                    log(
                                        SUMSUB_VERIFICATION_ACTIONS.SUMSUB_VERIFY_PARTNER,
                                        LOG_STATUSES.SUCCESS
                                    );
                                    if (res?.success) {
                                        redirectToPartnerOrConditions(
                                            navigate,
                                            partnerRedirectUrl,
                                            resolvePartnerInfo(response?.account, partner)
                                        );
                                    } else {
                                        navigate('/conditions');
                                    }
                                })
                                .catch((err) => {
                                    trackRegistrationFailed({
                                        step: 'sumsub',
                                        error: err,
                                    });
                                    handleApiError(err, navigate);
                                });
                        } else {
                            navigate('/conditions');
                        }
                    } else {
                        trackBiometricVerificationFailed({
                            verification_method: 'sumsub',
                            errorCode: 'SumSubError',
                            registrationFailedStep: 'sumsub',
                        });
                        navigate('/error?message=SumSubError');
                    }
                })
                .catch((err) => {
                    trackBiometricVerificationFailed({
                        verification_method: 'sumsub',
                        error: err,
                        fallback: 'SumSubError',
                        registrationFailedStep: 'sumsub',
                    });
                    handleApiError(err, navigate);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else if ('idCheck.onApplicantLoaded' === name) {
            applicantIdRef.current = e.applicantId;
        } else if (name === 'onError' || name === 'expirationHandler') {
            const errorCode =
                name === 'expirationHandler' ? 'SumSubExpired' : 'SumSubError';
            trackBiometricVerificationFailed({
                verification_method: 'sumsub',
                errorCode,
                registrationFailedStep: 'sumsub',
            });
            navigate(`/error?message=${errorCode}`);
        }
    };

    const handleBack = () => navigate(checkImIdEnabled() ? '/verification-options' : '/');

    return (
        <RegistrationPage
            headerProps={{ showBack: true, onBack: handleBack }}
            contentClassName="max-w-full xl:max-w-[40rem] !mt-9 mx-auto w-full"
        >
            {token && !isLoading ? (
                <div className="w-full flex-1">
                    <SumsubWebSdk
                        config={{
                            lang: i18n?.language === 'am' ? 'hy' : i18n?.language,
                        }}
                        accessToken={token}
                        expirationHandler={(e) => handler('expirationHandler', e)}
                        onMessage={(e, v) => handler(e, v)}
                        onError={(e) => handler('onError', e)}
                    />
                </div>
            ) : (
                <Loading className="flex-1 py-16" size={48} />
            )}
        </RegistrationPage>
    );
}
